import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';

const CDN = 'https://BelvoirCastle.b-cdn.net/Wine';
const HERO_IMG = `${CDN}/90ED9D8D3100A27451EA95B967DE4A79%20(1).webp`;
const CASTLE_VINES_IMG = `${CDN}/230614-Vineyard-Story.webp`;
const VINEYARD_IMG = `${CDN}/Belvoir%20Vineyards%20and%20Wine%20(94%20of%20187).webp`;
const GRAPES_IMG = `${CDN}/Belvoir%20Vineyards%20and%20Wine%20(71%20of%20187).webp`;
const HARVEST_IMG = `${CDN}/CSP_98A9972.webp`;
const BOTTLES_IMG = `${CDN}/Belvoir%20Vineyards%20and%20Wine%20(7%20of%20187).webp`;
const RANGE_IMG = `${CDN}/CSP_98A0057.webp`;
const PICNIC_IMG = `${CDN}/DSC08400.webp`;
const GROUP_IMG = `${CDN}/Photo%2028-08-2025%2C%2013%2036%2034.webp`;

const PAGE_PATH = 'about/belvoir-wines';
const FARM_SHOP_PATH = '/retail-village/shops/farm-shop';
const VINEYARD_TOUR_PATH = '/whats-on/vineyard-tour';
const GROUPS_PATH = '/your-visit/groups';
const AWARDS_POST_PATH = '/journal/belvoir-estate-wine-wins-four-awards-winegb-midlands-north';

const C = {
  navy: '#101d40',
  midnight: '#0A1428',
  gold: '#C9963C',
  goldDk: '#A67C2A',
  ivory: '#FAF7F0',
  parchment: '#F3EDD8',
  silver: '#9AA3AE',
  bronze: '#A9713F',
};

const STATS = [
  { value: '5', label: 'Acres of vines' },
  { value: '20,000', label: 'Bottles a year' },
  { value: '2019', label: 'Year planted' },
  { value: 'By hand', label: 'Every grape picked' },
];

const VINEYARD_POINTS = [
  {
    title: 'Iron sandstone soils',
    body: 'The vineyard sits on iron-rich sandstone, very particular to this part of the world, and gives Belvoir Wines its distinctive flavour. These soils drain freely and produce perfect grapes for making our range of award-winning still and sparkling, white and rosé wines.',
  },
  {
    title: 'Planted in 2019',
    body: 'The vineyard was established in 2019. In a cool and changeable British climate, the choice of grape variety is the most important decision in setting up a vineyard. Our varieties were selected for their proven ability to ripen reliably in these conditions, and that choice continues to shape the quality and consistency of our wines.',
  },
  {
    title: 'Five acres',
    body: 'The vineyard covers five acres and is tended every day by our vineyard manager David Crossland, who has worked on the Estate for 45 years. Dedicated volunteers help us with pruning and picking each year and we are incredibly grateful for their support.',
  },
  {
    title: 'Varieties chosen for the British climate',
    body: 'Most of the grapes we grow come from German breeding and cool-climate stock. German viticulture has spent decades developing vines that ripen early, cope with cool summers and stand up to a damp, unpredictable season.',
  },
];

const VARIETIES = [
  {
    name: 'Siegerrebe',
    body: 'Siegerrebe is a German-bred grape that ripens early, which makes it well suited to a British growing season. It is known for aromatic, floral wines with a distinctive perfume.',
  },
  {
    name: 'Seyval Blanc',
    body: 'Seyval Blanc is a hardy, reliable white grape that has been grown in England for many years. It copes well with cool, damp conditions and is known for fresh, crisp wines with citrus and orchard fruit character.',
  },
  {
    name: 'Pinot Précoce',
    body: 'Pinot Précoce is an early-ripening relative of Pinot Noir. It is known in Germany as Frühburgunder. Ripening early gives it an advantage in a cool climate, and it is grown for its red fruit character.',
  },
  {
    name: 'Solaris',
    body: 'Solaris is a German-bred white grape. It ripens early and holds up well against disease, which makes it a dependable choice for a British vineyard. It is known for ripe, fruit-forward wines.',
  },
];

const AWARDS = [
  { wine: 'Belvoir Rosé Sparkling 2022', medal: 'Silver', award: 'Silver Award for Best British Sparkling Rosé' },
  { wine: 'Belvoir White, Solaris, 2022', medal: 'Silver', award: 'Silver Award for Best British Still White' },
  { wine: 'Belvoir Rosé, 2023', medal: 'Silver', award: 'Silver Award for Best British Still Rosé' },
  { wine: 'Belvoir Sparkling, 2022', medal: 'Bronze', award: 'Bronze Award for Best British Sparkling Wine' },
];

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(560px, 78vh, 820px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: auto;
    min-height: clamp(560px, 82svh, 720px);
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 45%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 20, 40, 0.95) 0%,
    rgba(16, 29, 64, 0.78) 40%,
    rgba(16, 29, 64, 0.35) 66%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(1.75rem, 4.5vw, 3rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.55rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.4rem, 5.8vw, 4.2rem);
  font-weight: 700;
  line-height: 1.02;
  color: #fff;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);

  em {
    font-style: italic;
    color: ${C.gold};
    text-shadow: 0 1px 3px rgba(10, 20, 40, 0.75), 0 4px 16px rgba(10, 20, 40, 0.7);
  }
`;

const HeroStrapline = styled.p`
  margin: 0 0 0.5rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.35rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  max-width: 38rem;
  text-shadow: 0 1px 10px rgba(10, 20, 40, 0.45);
`;

const HeroNote = styled.p`
  margin: 0 0 1.4rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;

    a {
      width: 100%;
    }
  }
`;

const BtnPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: ${C.gold};
  color: ${C.navy};
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${C.goldDk};
    color: #fff;
    transform: translateY(-1px);
  }
`;

const BtnGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
`;

const BtnOutline = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.9rem 1.75rem;
  background: transparent;
  color: ${C.navy};
  border: 1.5px solid rgba(16, 29, 64, 0.35);
  border-radius: 6px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: ${C.navy};
    background: ${C.navy};
    color: #fff;
  }
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => (p.$border ? '1px solid rgba(16, 29, 64, 0.07)' : 'none')};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 2.5rem;
  height: 2px;
  background: ${C.gold};
  margin-bottom: 1.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${C.navy};

  em {
    font-style: italic;
    color: ${C.gold};
  }
`;

const SectionIntro = styled.p`
  margin: 0 0 1.25rem;
  max-width: 40rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.7vw, 1.05rem);
  line-height: 1.65;
  color: rgba(16, 29, 64, 0.68);

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${C.goldDk};
    font-weight: 600;
  }
`;

const SectionHead = styled.div`
  margin-bottom: clamp(1.75rem, 3.5vw, 2.75rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.75rem, 4vw, 3.5rem);
  align-items: ${p => p.$align || 'center'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Photo = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: ${p => (p.$fill ? 'auto' : p.$ratio || '3 / 2')};
  min-height: ${p => (p.$fill ? '320px' : '0')};
  order: ${p => (p.$first ? -1 : 0)};

  img {
    position: ${p => (p.$fill ? 'absolute' : 'static')};
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${p => p.$pos || 'center'};
    display: block;
  }

  @media (max-width: 860px) {
    order: 0;
    aspect-ratio: 3 / 2;
    min-height: 0;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 12px;
  overflow: hidden;
  margin-top: clamp(1.5rem, 3vw, 2rem);
`;

const Stat = styled.div`
  background: #fff;
  padding: clamp(1.1rem, 2.4vw, 1.5rem);
`;

const StatValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.6rem, 3.2vw, 2.1rem);
  font-weight: 700;
  line-height: 1.05;
  color: ${C.navy};
`;

const StatLabel = styled.div`
  margin-top: 0.3rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${C.goldDk};
`;

const PointList = styled.div`
  display: grid;
  gap: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const Point = styled.div`
  padding-left: 1.1rem;
  border-left: 2px solid rgba(201, 150, 60, 0.45);
`;

const PointTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: ${C.navy};
`;

const BodyText = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.93rem;
  line-height: 1.6;
  color: rgba(16, 29, 64, 0.66);
`;

const VarietyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const VarietyCard = styled.div`
  background: #fff;
  padding: clamp(1.4rem, 2.8vw, 1.9rem);
`;

const VarietyNumber = styled.div`
  margin-bottom: 0.5rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: ${C.gold};
`;

const AwardList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
`;

const AwardRow = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: clamp(1rem, 2.2vw, 1.35rem) clamp(1.1rem, 2.5vw, 1.6rem);

  & + & {
    border-top: 1px solid rgba(16, 29, 64, 0.08);
  }
`;

const Medal = styled.span`
  flex-shrink: 0;
  width: 4.6rem;
  padding: 0.35rem 0;
  border-radius: 999px;
  text-align: center;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #fff;
  background: ${p => (p.$medal === 'Bronze' ? C.bronze : C.silver)};
`;

const AwardWine = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${C.navy};
`;

const AwardName = styled.div`
  margin-top: 0.15rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.85rem;
  color: rgba(16, 29, 64, 0.62);
`;

const AwardsCaption = styled.p`
  margin: 0 0 0.75rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
`;

const TextLink = styled(Link)`
  display: inline-block;
  margin-top: 1.25rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${C.goldDk};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: ${C.navy};
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: rgba(16, 29, 64, 0.08);
  border-radius: 10px;
  overflow: hidden;
  margin: 1.5rem 0;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.1rem, 2.4vw, 1.5rem);
`;

const InfoLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.4);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const TourGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: clamp(1.25rem, 3vw, 2rem);

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const TourCard = styled.article`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(16, 29, 64, 0.06);
`;

const TourImage = styled.img`
  width: 100%;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  object-position: ${p => p.$pos || 'center'};
  display: block;
`;

const TourBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1.25rem;
  padding: clamp(1.4rem, 3vw, 2rem);

  a {
    align-self: flex-start;
    margin-top: auto;
  }

  @media (max-width: 520px) {
    a {
      align-self: stretch;
    }
  }
`;

const BelvoirWines = () => {
  const canonical = getCanonicalUrl(PAGE_PATH);
  const description =
    'Award-winning English wine grown and hand-picked on the Belvoir Estate in Leicestershire. Discover our vineyard, our four grape varieties and where to buy Belvoir wine.';

  return (
    <>
      <Helmet>
        <title>Belvoir Estate Wine | Award-Winning English Wine | Belvoir Castle</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Belvoir Estate Wine | Belvoir Castle" />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={HERO_IMG} />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'WebPage',
                '@id': `${canonical}#webpage`,
                url: canonical,
                name: 'Belvoir Estate Wine',
                description,
                primaryImageOfPage: HERO_IMG,
                isPartOf: { '@type': 'WebSite', name: 'Belvoir Castle', url: 'https://www.belvoircastle.com' },
              },
              {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.belvoircastle.com/' },
                  { '@type': 'ListItem', position: 2, name: 'About', item: 'https://www.belvoircastle.com/about' },
                  { '@type': 'ListItem', position: 3, name: 'Belvoir Wines', item: canonical },
                ],
              },
            ],
          })}
        </script>
      </Helmet>

      <Page>
        <HeroWrap>
          <HeroImg src={HERO_IMG} alt="Aerial view of the rows of vines in the Belvoir Estate vineyard" />
          <HeroOverlay />
          <HeroContent>
            <HeroKicker>The Belvoir Estate · Leicestershire</HeroKicker>
            <HeroTitle>
              Belvoir Estate <em>Wine</em>
            </HeroTitle>
            <HeroStrapline>
              Award-winning wines, grown and harvested on the Belvoir Estate in Leicestershire.
            </HeroStrapline>
            <HeroNote>Available to buy from Belvoir Farm Shop.</HeroNote>
            <HeroButtons>
              <BtnPrimary to={FARM_SHOP_PATH}>Visit the Farm Shop</BtnPrimary>
              <BtnGhost to={VINEYARD_TOUR_PATH}>Vineyard Tours</BtnGhost>
            </HeroButtons>
          </HeroContent>
        </HeroWrap>

        <Section $bg={C.ivory} $border>
          <Inner>
            <TwoCols $align="stretch">
              <div>
                <Kicker>Grown in the Vale of Belvoir</Kicker>
                <Divider />
                <SectionTitle>
                  Wine from the <em>Belvoir Estate</em>
                </SectionTitle>
                <SectionIntro>
                  Belvoir Castle stands above the Vale of Belvoir in Leicestershire. We have grown five acres of grapes, picked by hand and producing around 20,000 bottles of sparkling and still wine each year, since 2019.
                </SectionIntro>
                <StatGrid>
                  {STATS.map(stat => (
                    <Stat key={stat.label}>
                      <StatValue>{stat.value}</StatValue>
                      <StatLabel>{stat.label}</StatLabel>
                    </Stat>
                  ))}
                </StatGrid>
              </div>
              <Photo $fill $pos="center 45%">
                <img
                  src={CASTLE_VINES_IMG}
                  alt="Belvoir Castle on the hill above the vines of the Belvoir Estate vineyard"
                  loading="lazy"
                />
              </Photo>
            </TwoCols>
          </Inner>
        </Section>

        <Section $bg="#fff" $border>
          <Inner>
            <SectionHead>
              <Kicker>Iron sandstone · Five acres</Kicker>
              <Divider />
              <SectionTitle>
                The <em>Vineyard</em>
              </SectionTitle>
            </SectionHead>
            <TwoCols $align="stretch">
              <Photo $first $fill $pos="center 60%">
                <img
                  src={VINEYARD_IMG}
                  alt="Pickers working along the rows of vines in the Belvoir Estate vineyard under a summer sky"
                  loading="lazy"
                />
              </Photo>
              <PointList>
                {VINEYARD_POINTS.map(point => (
                  <Point key={point.title}>
                    <PointTitle>{point.title}</PointTitle>
                    <BodyText>{point.body}</BodyText>
                  </Point>
                ))}
              </PointList>
            </TwoCols>
          </Inner>
        </Section>

        <Section $bg={C.ivory} $border>
          <Inner>
            <SectionHead>
              <Kicker>Grape varieties</Kicker>
              <Divider />
              <SectionTitle>
                Our four <em>varieties</em>
              </SectionTitle>
              <SectionIntro>
                Four grapes make up the Belvoir vineyard. Each has its own character, and each suits the site and the soil type.
              </SectionIntro>
            </SectionHead>
            <TwoCols $align="stretch">
              <Photo $first $fill $pos="center 70%">
                <img
                  src={GRAPES_IMG}
                  alt="A bucket of freshly hand-picked black grapes in the Belvoir Estate vineyard"
                  loading="lazy"
                />
              </Photo>
              <VarietyGrid>
                {VARIETIES.map((variety, index) => (
                  <VarietyCard key={variety.name}>
                    <VarietyNumber>{String(index + 1).padStart(2, '0')}</VarietyNumber>
                    <PointTitle>{variety.name}</PointTitle>
                    <BodyText>{variety.body}</BodyText>
                  </VarietyCard>
                ))}
              </VarietyGrid>
            </TwoCols>
          </Inner>
        </Section>

        <Section $bg="#fff" $border>
          <Inner>
            <TwoCols>
              <div>
                <Kicker>Harvest 2026</Kicker>
                <Divider />
                <SectionTitle>
                  Harvested <em>by hand</em>
                </SectionTitle>
                <SectionIntro>
                  Every grape at Belvoir is picked by hand. Hand harvesting means each bunch is assessed as it is picked, so only fruit in good condition is used.
                </SectionIntro>
                <SectionIntro>
                  Harvest is the end of a long year in the vineyard, and it takes a team. The 2026 harvest was a bumper crop due to the hot summer weather we have had this year, consistent across all of our varieties, which is a result we are very pleased with.
                </SectionIntro>
              </div>
              <Photo>
                <img
                  src={HARVEST_IMG}
                  alt="Volunteers hand-picking grapes between the vines during the Belvoir Estate harvest"
                  loading="lazy"
                />
              </Photo>
            </TwoCols>
          </Inner>
        </Section>

        <Section $bg={C.parchment} $border>
          <Inner>
            <SectionHead>
              <Kicker>WineGB Midlands and North 2026</Kicker>
              <Divider />
              <SectionTitle>
                Award-winning <em>wines</em>
              </SectionTitle>
              <SectionIntro>
                In 2026, all four Belvoir wines won awards at the WineGB Midlands and North awards. WineGB is the trade body for the English and Welsh wine industry, and its regional awards are judged on quality. Winning with every wine we entered is a strong result for a vineyard planted in 2019.
              </SectionIntro>
            </SectionHead>
            <TwoCols>
              <div>
                <AwardsCaption>WineGB Midlands and North 2026</AwardsCaption>
                <AwardList>
                  {AWARDS.map(item => (
                    <AwardRow key={item.wine}>
                      <Medal $medal={item.medal}>{item.medal}</Medal>
                      <div>
                        <AwardWine>{item.wine}</AwardWine>
                        <AwardName>{item.award}</AwardName>
                      </div>
                    </AwardRow>
                  ))}
                </AwardList>
                <TextLink to={AWARDS_POST_PATH}>Read the full story in our journal</TextLink>
              </div>
              <Photo>
                <img
                  src={BOTTLES_IMG}
                  alt="The four award-winning Belvoir Estate wines on a wooden crate in the vineyard"
                  loading="lazy"
                />
              </Photo>
            </TwoCols>
          </Inner>
        </Section>

        <Section $bg="#fff" $border>
          <Inner>
            <TwoCols>
              <Photo $first>
                <img
                  src={RANGE_IMG}
                  alt="The Belvoir Castle range of sparkling, rosé, white and red wines displayed on a table with seasonal foliage"
                  loading="lazy"
                />
              </Photo>
              <div>
                <Kicker>Belvoir Farm Shop</Kicker>
                <Divider />
                <SectionTitle>
                  Where to <em>buy</em>
                </SectionTitle>
                <SectionIntro>
                  Belvoir wine is available to purchase from Belvoir Farm Shop. If you would like to taste the wines that won awards in 2026, the farm shop is the place to find them.
                </SectionIntro>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Opening hours</InfoLabel>
                    <InfoValue>
                      Monday – Sunday
                      <br />
                      9am – 5pm
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Location</InfoLabel>
                    <InfoValue>
                      Belvoir Farm Shop
                      <br />
                      Belvoir Retail Village
                    </InfoValue>
                  </InfoItem>
                </InfoGrid>
                <BtnPrimary to={FARM_SHOP_PATH}>Visit the Farm Shop</BtnPrimary>
              </div>
            </TwoCols>
          </Inner>
        </Section>

        <Section $bg={C.ivory}>
          <Inner>
            <SectionHead>
              <Kicker>Visit the vineyard</Kicker>
              <Divider />
              <SectionTitle>
                Vineyard <em>tours</em>
              </SectionTitle>
            </SectionHead>
            <TourGrid>
              <TourCard>
                <TourImage
                  src={PICNIC_IMG}
                  $pos="center 65%"
                  alt="Belvoir Estate sparkling wines with glasses on a picnic hamper in the vineyard"
                  loading="lazy"
                />
                <TourBody>
                  <div>
                    <PointTitle>Vineyard Tours</PointTitle>
                    <BodyText>
                      The best way to understand the wine is to see where it comes from. We run vineyard tours on selected days throughout the summer. You will see the vines, the iron sandstone soils they grow in and hear how each of our four varieties are grown. On a hill, with a beautiful view of Belvoir Castle in the distance, it’s the perfect place to enjoy a glass of award-winning wine.
                    </BodyText>
                  </div>
                  <BtnPrimary to={VINEYARD_TOUR_PATH}>Book a vineyard tour</BtnPrimary>
                </TourBody>
              </TourCard>
              <TourCard>
                <TourImage
                  src={GROUP_IMG}
                  alt="A group of visitors with their dogs on a tour of the Belvoir Estate vineyard"
                  loading="lazy"
                />
                <TourBody>
                  <div>
                    <PointTitle>Private Group Tours</PointTitle>
                    <BodyText>
                      Vineyard tours are available to book for private groups and coach tours. If you are organising a group visit, get in touch and we will talk you through what is possible.
                    </BodyText>
                  </div>
                  <BtnOutline to={GROUPS_PATH}>Enquire about group visits</BtnOutline>
                </TourBody>
              </TourCard>
            </TourGrid>
          </Inner>
        </Section>

        <Footer />
      </Page>
    </>
  );
};

export default BelvoirWines;
